// The small glyphs the hazard details panel still draws inline (as SVG path
// data on a 24×24 grid, the iOS asset vectors): the tick on the "Confirmed"
// tag and the clock on the "Not confirmed yet." card.
//
// The hazard TYPE icons are no longer drawn here: since hazard icons v2 they
// are the HiiKER sprite's warning triangles (hazardSprite.ts, bundled PNGs in
// hazardIconAssets.ts), on the map and off it.
//
// Every glyph is a stroked outline: no fill, round caps and joins.

export interface HazardIconDef {
  paths: readonly string[];
  strokeWidth: number;
}

export const HAZARD_ICON_VIEWBOX = 24;

/** A circle as path data (two arcs), for <path d>. */
function circle(cx: number, cy: number, r: number): string {
  return `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0`;
}

const icon = (paths: string[], strokeWidth = 2): HazardIconDef => Object.freeze({ paths: Object.freeze(paths), strokeWidth });

/** The tick on the "Confirmed" tag (iOS hazard_check). */
export const HAZARD_CHECK_ICON: HazardIconDef = icon(['M5 12.5l4.5 4.5L19 7.5'], 3);

/** The clock on the "Not confirmed yet." card (iOS hazard_clock). */
export const HAZARD_CLOCK_ICON: HazardIconDef = icon([circle(12, 12, 9), 'M12 7v5l3 2'], 2.2);

/**
 * The HiiKER profile placeholder's mark (the apps' `profile_placeholder`), drawn
 * dark on a yellow circle in a 0 0 89 89 box: a confirmer with no photo and no
 * name to take initials from.
 */
export const HIIKER_AVATAR_PLACEHOLDER_PATH =
  'M69.0192 62.4532C67.8995 63.5315 58.5119 55.1257 44.8189 54.8848C30.3186 54.6266 20.5202 63.7601 19.3512 62.4532C18.1822 61.1462 38.8708 26.7124 39.7323 26.7153C40.5938 26.7182 46.2934 37.6355 46.9541 37.5411C47.6148 37.4467 50.2575 31.9725 51.2726 31.5558C52.2876 31.1391 70.139 61.3748 69.0192 62.4532ZM51.2856 23.7809C49.8664 23.7809 48.7163 24.9211 48.7163 26.3281C48.7163 27.735 49.8664 28.8752 51.2856 28.8752C52.7048 28.8752 53.8549 27.735 53.8549 26.3281C53.8549 24.9211 52.7048 23.7809 51.2856 23.7809ZM39.7062 18.8372C38.287 18.8372 37.1369 19.9774 37.1369 21.3843C37.1369 22.7913 38.287 23.9315 39.7062 23.9315C41.1254 23.9315 42.2756 22.7913 42.2756 21.3843C42.2756 19.9774 41.1254 18.8372 39.7062 18.8372ZM55.0571 41.0432C54.9598 40.6355 51.9031 34.7159 51.2899 34.6666C50.6768 34.6174 48.8279 38.0743 48.8812 38.6939C48.9344 39.3135 49.6947 41.2776 50.0921 41.2239C50.4894 41.1701 51.1455 39.2809 51.5243 39.3135C51.9031 39.3461 53.6479 42.8321 54.1891 42.7728C54.7304 42.7136 55.1544 41.4508 55.0571 41.0432ZM43.1349 35.7079C43.1385 34.8562 40.1699 29.6647 39.7366 29.6067C39.3034 29.5487 36.2108 34.7699 36.2819 35.7251C36.353 36.6802 36.9672 37.3126 37.3669 37.3472C37.7667 37.3818 38.4651 35.4131 38.9641 35.4153C39.463 35.4174 41.0183 38.585 41.4423 38.4745C41.8662 38.3639 43.1313 36.5595 43.1349 35.7079Z';
